import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import de from '@angular/common/locales/de';
import en from '@angular/common/locales/en';
import zh from '@angular/common/locales/zh';
import {
  ErrorHandler,
  LOCALE_ID,
  enableProdMode,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router, UrlSerializer } from '@angular/router';
import { IconDefinition } from '@ant-design/icons-angular';
import { IsPortalApp } from '@app/core/services/utilities.service';
import {
  detectBaseHref,
  getCurrentStage,
  sanitizeNullQueryParams,
  sentryTracePropogationURL,
  setPortalParams,
} from '@app/core/utilities/url.utilities';
import { AccordionModule } from '@app/shared/modules/accordion/accordion.module';
import { DisallowedOptimizationModule } from '@app/shared/modules/disallowed-optimization/disallowed-optimization.module';
import { environment } from '@env/environment';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { NZ_DATE_CONFIG, NZ_I18N } from 'ng-zorro-antd/i18n';
import { NZ_ICONS, NzIconModule } from 'ng-zorro-antd/icon';
import { AppPreloadingStrategy } from './app/app-preloading-strategy';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { CoreModule } from './app/core/core.module';
import { initializeAppFactory, transformLegacyParamsFactory } from './app/core/factories/initialize-app.factory';
import { getLocaleFactory, getLocaleIdFactory } from './app/core/factories/locale.factory';
import { CustomerSummary } from './app/core/models/common.models';
import { Customer } from './app/core/models/customer.models';
import { User } from './app/core/models/user.models';
import { CustomUrlSerializer } from './app/core/services/custom-url-serializer';
import { initializeHighcharts } from './app/core/utilities/highcharts';
import { SubscriptionSharedModule } from './app/settings/shared/modules/subscription/subscription-shared.module';
import { HttpLoaderFactory, SharedModule } from './app/shared/shared.module';

declare global {
  interface Window {
    grecaptcha: any;
    adspert_data: {
      user?: User;
      customerSummary?: CustomerSummary;
      customer?: Customer;
    };
  }
}

Sentry.init({
  dsn: environment.app.sentry.key,
  environment: getCurrentStage(),
  release: `apollo@${environment.app.version.slice(0, -2)}`,
  enabled: environment.production,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  tracesSampleRate: 0.5,
  tracePropagationTargets: [sentryTracePropogationURL()],
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 0.1,
});

window.adspert_data = window.adspert_data || {};
window.adspert_data.user = {} as User;

if (environment.production) {
  enableProdMode();
}

const boot = () => {
  const icons: IconDefinition[] = [
    {
      icon: `<svg viewBox="0 0 32 32"> <path d="M12.994 14a1 1 0 0 1 1-1h1a2 2 0 0 1 2 2v5a1 1 0 0 0 1 1h1a1 1 0 0 1 0 2h-1a3 3 0 0 1-3-3v-5h-1a1 1 0 0 1-1-1Zm1.666-5.747a1.5 1.5 0 1 1 1.667 2.494 1.5 1.5 0 0 1-1.667-2.494Zm1.025 1.709a.498.498 0 0 0 .299-.56.5.5 0 1 0-.299.56Z"></path><path d="M15.994 2c-7.732 0-14 6.268-14 14s6.268 14 14 14 14-6.268 14-14-6.268-14-14-14Zm-16 14c0-8.837 7.163-16 16-16 8.836 0 16 7.163 16 16s-7.164 16-16 16c-8.837 0-16-7.163-16-16Z"></path></svg>`,
      name: 'exclamation-circle',
      theme: 'outline',
    },
    {
      icon: `<svg viewBox="0 0 24 24"><path d="M12,24c-0.088,0-0.175-0.016-0.26-0.047c-0.008-0.003-0.015-0.005-0.022-0.008c-0.096-0.039-0.179-0.094-0.249-0.165 L0.97,13.28c-0.142-0.142-0.22-0.33-0.22-0.53s0.078-0.389,0.22-0.53C1.111,12.078,1.3,12,1.5,12s0.389,0.078,0.53,0.22l9.22,9.22 V0.75C11.25,0.336,11.586,0,12,0s0.75,0.336,0.75,0.75v20.689l9.22-9.22C22.111,12.078,22.3,12,22.5,12s0.389,0.078,0.53,0.22 c0.142,0.142,0.22,0.33,0.22,0.53s-0.078,0.389-0.22,0.53l-10.5,10.5c-0.07,0.07-0.153,0.125-0.246,0.164 c-0.008,0.003-0.018,0.007-0.027,0.01C12.172,23.985,12.086,24,12,24z"></path></svg>`,
      name: 'caret-down',
      theme: 'outline',
    },
    {
      icon: `<svg viewBox="0 0 24 24"><path d="M12,24c-0.414,0-0.75-0.336-0.75-0.75V2.561l-9.22,9.22C1.889,11.922,1.7,12,1.5,12s-0.389-0.078-0.53-0.22 c-0.142-0.141-0.22-0.33-0.22-0.53s0.078-0.389,0.22-0.53L11.469,0.22c0.07-0.07,0.152-0.125,0.245-0.163 c0.008-0.003,0.016-0.006,0.025-0.009C11.825,0.016,11.912,0,12,0c0.089,0,0.177,0.016,0.261,0.048 c0.007,0.002,0.014,0.005,0.021,0.007c0.097,0.04,0.179,0.095,0.25,0.166L23.03,10.72c0.142,0.141,0.22,0.33,0.22,0.53 s-0.078,0.389-0.22,0.53S22.7,12,22.5,12s-0.389-0.078-0.53-0.22l-9.22-9.22V23.25C12.75,23.664,12.414,24,12,24z"></path></svg>`,
      name: 'caret-up',
      theme: 'outline',
    },
    {
      icon: `<svg viewBox="0 0 24 24"><path d="M5.499,24c-0.2,0-0.389-0.078-0.53-0.22c-0.292-0.292-0.292-0.768,0-1.061L15.689,12L4.969,1.28 c-0.142-0.141-0.22-0.33-0.22-0.53s0.078-0.389,0.22-0.53C5.11,0.078,5.299,0,5.499,0s0.389,0.078,0.53,0.22l10.72,10.72 c0.584,0.584,0.585,1.535,0.001,2.119L6.029,23.78C5.888,23.922,5.699,24,5.499,24z"></path></svg>`,
      name: 'right',
      theme: 'outline',
    },
    {
      icon: `<svg viewBox="0 0 24 24"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 13.0597L1.28033 23.7794C0.987437 24.0722 0.512563 24.0722 0.21967 23.7794C-0.0732233 23.4865 -0.0732233 23.0116 0.21967 22.7187L10.9393 11.999L0.21967 1.27935C-0.0732233 0.98646 -0.0732233 0.511587 0.21967 0.218693C0.512563 -0.0741999 0.987437 -0.0741999 1.28033 0.218693L12 10.9384L22.7197 0.218693C23.0126 -0.0741999 23.4874 -0.0741999 23.7803 0.218693C24.0732 0.511587 24.0732 0.98646 23.7803 1.27935L13.0607 11.999L23.7803 22.7187C24.0732 23.0116 24.0732 23.4865 23.7803 23.7794C23.4874 24.0722 23.0126 24.0722 22.7197 23.7794L12 13.0597Z"/></svg>`,
      name: 'cross',
      theme: 'outline',
    },
    {
      icon: `<svg viewBox="0 0 32 32"><path d="M16 24.783a2.65 2.65 0 0 1-1.882-.777L.389 10.278a1.337 1.337 0 0 1 0-1.887 1.331 1.331 0 0 1 1.885 0l13.727 13.727L29.726 8.39c.252-.252.587-.391.942-.391.356 0 .692.139.943.391.519.52.519 1.366 0 1.887L17.884 24.004a2.641 2.641 0 0 1-1.883.78Z"></path></svg>`,
      name: 'down',
      theme: 'outline',
    },
  ];

  initializeHighcharts();
  registerLocaleData(en);
  registerLocaleData(de);
  registerLocaleData(zh);
  bootstrapApplication(AppComponent, {
    providers: [
      { provide: APP_BASE_HREF, useFactory: detectBaseHref },
      importProvidersFrom(
        BrowserModule,
        SharedModule,
        CoreModule,
        TranslateModule.forRoot({
          loader: { provide: TranslateLoader, useFactory: HttpLoaderFactory, deps: [HttpClient] },
          isolate: false,
        }),
        AppRoutingModule,
        SubscriptionSharedModule,
        AccordionModule,
        NzIconModule,
        DisallowedOptimizationModule,
      ),
      { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
      { provide: Sentry.TraceService, deps: [Router] },
      provideAppInitializer(() => ((_) => () => {})(inject(Sentry.TraceService))()),
      provideAppInitializer(transformLegacyParamsFactory()),
      provideAppInitializer(() => initializeAppFactory(inject(Store), inject(TranslateService))()),
      { provide: NZ_I18N, useFactory: getLocaleFactory, deps: [TranslateService] },
      { provide: LOCALE_ID, useFactory: getLocaleIdFactory, deps: [TranslateService] },
      { provide: NZ_DATE_CONFIG, useValue: { firstDayOfWeek: 1 } },
      { provide: NZ_ICONS, useValue: icons },
      { provide: UrlSerializer, useClass: CustomUrlSerializer },
      AppPreloadingStrategy,
      provideAnimations(),
    ],
  }).catch((err) => console.error(err));
};

(function sanitizeQueryParamsAndBoot() {
  try {
    if (IsPortalApp()) {
      setPortalParams();
      boot();
    } else {
      // remove null query Params
      const params = sanitizeNullQueryParams();
      if (params) location.search = `?${params.join('&')}`;
      else boot();
    }
  } catch (error) {
    boot();
  }
})();
